import { useCollection } from '@amzn/awsui-collection-hooks';
import {
  Box,
  CollectionPreferences,
  Container,
  Header,
  Link,
  Pagination,
  Table,
} from '@amzn/awsui-components-react-v3';
import React, { ReactElement, useEffect, useState } from 'react';
import { CollectionPreferencesProps } from '@amzn/awsui-components-react-v3/uxdg';
import { useTranslation } from 'react-i18next';
import { ceil } from 'lodash';
import { PartsInfo } from '../../../services/container-management/types';
import { moveContainerFeatureFlag } from '../../../utils/featureFlags';

export type PartsOptions = {
  pageSize: number;
};

const PartsDetail = (props: {
  parts: PartsInfo[];
  totalParts: number;
  loadingParts: boolean;
  pageIndex: number;
  setPageIndex: (index: number) => void;
  setPartsOptions: (setPartsOptions: PartsOptions) => void;
}): ReactElement => {
  const { parts, totalParts, loadingParts, pageIndex, setPageIndex, setPartsOptions } = props;
  const { t } = useTranslation();
  const isNonProdStage = moveContainerFeatureFlag();

  const columnDefinitions = [
    {
      id: 'serialId',
      header: 'Serial ID',
      cell: (item: PartsInfo) => {
        const BASE_URL_MOBILITY_PARTS = {
          Test: `https://mobility-beta.amazon.com/part/search?search_type=all&search_string=${item.serialId}&max_rows=50&query=GO`,
          Release: `https://mobility.amazon.com/part/search?search_type=all&search_string=${item.serialId}&max_rows=50&query=GO`,
        };
        return item.serialId ? (
          <Link
            external
            href={isNonProdStage ? BASE_URL_MOBILITY_PARTS.Test : BASE_URL_MOBILITY_PARTS.Release}
          >
            {item.serialId}
          </Link>
        ) : (
          '-'
        );
      },
      sortingField: 'serialId',
    },
    {
      id: 'model',
      header: 'Model',
      cell: (item: PartsInfo) => item.model || '-',
      sortingField: 'model',
    },
    {
      id: 'vendor',
      header: 'Vendor',
      cell: (item: PartsInfo) => item.vendor || '-',
      sortingField: 'vendor',
    },
    {
      id: 'type',
      header: 'Type',
      cell: (item: PartsInfo) => item.type || '-',
      sortingField: 'type',
    },
    {
      id: 'state',
      header: 'State',
      cell: (item: PartsInfo) => item.state || '-',
      sortingField: 'state',
    },
  ];

  const [preferences, setPreferences] = useState<CollectionPreferencesProps.Preferences>({
    pageSize: 10,
    visibleContent: ['serialId', 'model', 'vendor', 'type', 'state'],
  });

  const { items, collectionProps, paginationProps } = useCollection(parts, {
    pagination: { pageSize: preferences.pageSize },
    sorting: { defaultState: { sortingColumn: columnDefinitions[0] } },
  });

  useEffect(() => {
    setPartsOptions({
      pageSize: preferences.pageSize ?? 10,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageIndex, preferences.pageSize]);

  return (
    <div>
      {items.length > 0 && (
        <Table
          {...collectionProps}
          columnDefinitions={columnDefinitions}
          items={items}
          header={<Header> {t('Parts')}</Header>}
          visibleColumns={preferences.visibleContent}
          pagination={
            <Pagination
              {...paginationProps}
              currentPageIndex={pageIndex}
              pagesCount={ceil(totalParts / (preferences.pageSize ?? 10))}
              ariaLabels={{
                nextPageLabel: t('Next page'),
                previousPageLabel: t('Previous page'),
                pageLabel: (pageNum) => t(`Page ${pageNum} of all pages`),
              }}
              onChange={({ detail }) => {
                setPageIndex(detail.currentPageIndex);
              }}
            />
          }
          loading={loadingParts}
          preferences={
            <CollectionPreferences
              title={t('Preferences')}
              onConfirm={({ detail }) => {
                setPreferences(detail);
                setPartsOptions({
                  pageSize: detail.pageSize ?? 10,
                });
                setPageIndex(1);
              }}
              confirmLabel={t('Confirm')}
              cancelLabel={t('Cancel')}
              preferences={preferences}
              pageSizePreference={{
                title: t('Select page size'),
                options: [
                  { value: 5, label: t('5 items') },
                  { value: 10, label: t('10 items') },
                  { value: 20, label: t('20 items') },
                  { value: 50, label: t('50 items') },
                  { value: 100, label: t('100 items') },
                  { value: 1000, label: t('1000 items') },
                ],
              }}
              visibleContentPreference={{
                title: t('Select visible content'),
                options: [
                  {
                    label: t('Main distribution properties'),
                    options: [
                      {
                        id: 'serialId',
                        label: t('Serial Id'),
                        editable: false,
                      },
                      { id: 'model', label: t('Model') },
                      { id: 'vendor', label: t('Vendor') },
                      { id: 'type', label: t('Type') },
                      {
                        id: 'state',
                        label: t('State'),
                      },
                    ],
                  },
                ],
              }}
            />
          }
        />
      )}
      {items.length === 0 && (
        <Container header={<Header> {t('Parts')}</Header>}>
          <Box color="text-status-inactive" textAlign="center">
            {t('No Parts to display')}
          </Box>
        </Container>
      )}
    </div>
  );
};

export default PartsDetail;
